import { Component, Prop, Vue } from "vue-property-decorator";
import { IndicatorOrTag } from "../../models/IndicatorOrTag";
import { getDynamicItemSize } from "../DraggableWrapper/utils/getDynamicItemSize";
declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback);
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}

type ResizeObserverCallback = (
  entries: ResizeObserverEntry[],
  observer: ResizeObserver
) => void;

interface ResizeObserverEntry {
  readonly target: Element;
  readonly contentRect: DOMRectReadOnly;
}

@Component({
  name: "IndicatorView"
})
export default class IndicatorView extends Vue {
  @Prop() element!: IndicatorOrTag;

  fontSizeH2 = 16;
  fontSizeH4 = 12;
  observer?: ResizeObserver;

  mounted() {
    const container = this.$refs.container as HTMLElement;
    if (!container) return;
    this.$nextTick(() => {
      this.adjustFont(container);

      const parent = container.parentElement || container;
      this.observer = new ResizeObserver(() => this.adjustFont(container));
      this.observer.observe(parent);

      // Forzar múltiples intentos de cálculo para asegurar dimensiones correctas
      this.forceInitialResize(container);
    });
  }

  private forceInitialResize(container: HTMLElement) {
    // Primer intento inmediato
    requestAnimationFrame(() => {
      this.adjustFont(container);

      // Segundo intento después del primer frame
      requestAnimationFrame(() => {
        this.adjustFont(container);

        // Tercer intento con timeout como fallback
        setTimeout(() => {
          this.adjustFont(container);
        }, 100);
      });
    });
  }

  beforeDestroy() {
    this.observer?.disconnect();
  }

  adjustFont(container: HTMLElement) {
    const { width, height } = getDynamicItemSize(container);
    if (width <= 0 || height <= 0) return;
    // Consider text length to avoid overflow. Use h4 as reference.
    const textH2 = String(this.element?.value ?? "");
    const textH4 = String(this.element?.indicatorOrTagName ?? "");
    const charsH2 = Math.max(1, textH2.length + 2);
    const charsH4 = Math.max(1, textH4.length);

    const padding = 16;
    const approxCharWidthFactor = 0.55;
    const maxWidth = width - padding * 2;
    const minFontSize = 10;
    let fontSizeH2 = Math.max(minFontSize, Math.min(width * 0.3, height * 0.5));
    let fontSizeH4 = Math.max(minFontSize, Math.min(width * 0.2, height * 0.3));

    const approxWidthH4 = fontSizeH4 * approxCharWidthFactor * charsH4;
    if (approxWidthH4 > maxWidth) {
      const scale = approxWidthH4 > 0 ? maxWidth / approxWidthH4 : 1;
      fontSizeH4 *= scale;
      fontSizeH2 *= scale;
    }

    const approxWidthH2 = fontSizeH2 * approxCharWidthFactor * charsH2;
    if (approxWidthH2 > maxWidth) {
      const scale = approxWidthH2 > 0 ? maxWidth / approxWidthH2 : 1;
      fontSizeH4 *= scale;
      fontSizeH2 *= scale;
    }

    this.fontSizeH2 = fontSizeH2;
    this.fontSizeH4 = fontSizeH4;
  }
}
